// ============================================================================
// MÓDULO: imageToPdfPipeline.js — v1.0 (Passo 70 — Pipeline PDF-First)
// OBJETIVO: Converter imagens (HEIC/JPEG/PNG) em PDF A4 otimizado (<3MB)
//           client-side, antes do upload para o Google Drive / PJe / e-Proc.
//
// ESTEIRA (3 FASES):
//   Fase A — HEIC → JPEG  (heic2any)
//   Fase B — Compressão   (browser-image-compression, max 1.5MB / 1920px)
//   Fase C — Encapsulamento em PDF A4  (jsPDF)
//
// GARANTIA: Cada fase possui try/catch independente.
//           Se falhar, o arquivo da fase anterior é preservado (Fallback).
// ============================================================================

import heic2any from 'heic2any';
import imageCompression from 'browser-image-compression';
// jsPDF importado dinamicamente (lazy) — uniformiza com ContractGenerator/ConsentTermGenerator/DocumentProcessor
// e permite ao Rollup isolar jspdf em chunk próprio (fora do bundle crítico do taskpane Word).

// ── Helpers ──────────────────────────────────────────────────────────────────

const isHeicFile = (file) => {
  const type = String(file?.type || '').toLowerCase();
  const name = String(file?.name || '').toLowerCase();
  return (
    type === 'image/heic' ||
    type === 'image/heif' ||
    name.endsWith('.heic') ||
    name.endsWith('.heif')
  );
};

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(String(e.target?.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const loadImage = (dataUrl) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });

// ── Pipeline Principal ────────────────────────────────────────────────────────

/**
 * Converte qualquer imagem (HEIC, JPEG, PNG, WebP) num PDF A4 comprimido.
 * Se qualquer fase falhar, retorna o melhor arquivo disponível (Fallback Seguro).
 *
 * @param {File} rawFile — arquivo original do usuário
 * @returns {Promise<File>} — File com .pdf ou o rawFile original em caso de falha total
 */
export async function processFileToPdf(rawFile) {
  if (!rawFile) return rawFile;

  const isImage = rawFile.type.startsWith('image/') || isHeicFile(rawFile);
  if (!isImage) return rawFile;

  let workFile = rawFile;

  // ── FASE A: HEIC → JPEG ───────────────────────────────────────────────────
  if (isHeicFile(workFile)) {
    try {
      const result = await heic2any({
        blob: workFile,
        toType: 'image/jpeg',
        quality: 0.85,
      });
      const jpegBlob = Array.isArray(result) ? result[0] : result;
      const jpegName = workFile.name.replace(/\.(heic|heif)$/i, '.jpg');
      workFile = new File([jpegBlob], jpegName, { type: 'image/jpeg' });
    } catch (heicErr) {
      console.warn('[imageToPdfPipeline] Fase A (HEIC→JPEG) falhou — usando original:', heicErr);
    }
  }

  // ── FASE B: COMPRESSÃO ────────────────────────────────────────────────────
  if (workFile.type.startsWith('image/')) {
    try {
      const compressed = await imageCompression(workFile, {
        maxSizeMB: 1.5,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
        fileType: 'image/jpeg',
        initialQuality: 0.85,
      });
      workFile = new File([compressed], workFile.name, { type: 'image/jpeg' });
    } catch (compErr) {
      console.warn('[imageToPdfPipeline] Fase B (Compressão) falhou — usando arquivo anterior:', compErr);
    }
  }

  // ── FASE C: ENCAPSULAMENTO PDF A4 ─────────────────────────────────────────
  if (workFile.type.startsWith('image/')) {
    try {
      const dataUrl = await fileToDataUrl(workFile);
      const img = await loadImage(dataUrl);

      const A4_W = 595.28;
      const A4_H = 841.89;
      const MARGIN = 20;
      const maxW = A4_W - MARGIN * 2;
      const maxH = A4_H - MARGIN * 2;

      const ratio = Math.min(maxW / img.naturalWidth, maxH / img.naturalHeight);
      const imgW = img.naturalWidth * ratio;
      const imgH = img.naturalHeight * ratio;
      const x = MARGIN + (maxW - imgW) / 2;
      const y = MARGIN + (maxH - imgH) / 2;

      const { jsPDF } = await import('jspdf');
      const pdf = new jsPDF({ orientation: 'p', unit: 'pt', format: 'a4', compress: true });
      pdf.addImage(dataUrl, 'JPEG', x, y, imgW, imgH, undefined, 'FAST');

      const pdfBlob = pdf.output('blob');
      const baseName = workFile.name.replace(/\.[^.]+$/, '');
      return new File([pdfBlob], `${baseName}.pdf`, { type: 'application/pdf' });
    } catch (pdfErr) {
      console.warn('[imageToPdfPipeline] Fase C (PDF A4) falhou — usando arquivo comprimido:', pdfErr);
    }
  }

  return workFile;
}
